import React from 'react';
import { useFitnessWizard } from '../../contexts/FitnessWizardContext';
import { ThemedButton } from '../ThemedButton';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Shield, CheckCircle2, Calendar } from 'lucide-react';

const GL_LIMITS = [
  { occurrence: '$1,000,000', aggregate: '$2,000,000' },
  { occurrence: '$2,000,000', aggregate: '$4,000,000' },
  { occurrence: '$2,000,000', aggregate: '$5,000,000' }
];

const OPTIONAL_COVERAGES = [
  { value: 'directors_officers', label: 'Directors & Officers (D&O) Liability' },
  { value: 'property', label: 'Property Coverage' },
  { value: 'event', label: 'Special Event Coverage' },
  { value: 'cyber', label: 'Cyber Liability' },
  { value: 'professional', label: 'Professional Liability' }
];

const FitnessCoverage = () => {
  const { formData, updateFormData, nextStep } = useFitnessWizard();

  const handleSubmit = (e) => {
    e.preventDefault();
    nextStep();
  };

  const handleOptionalCoverageToggle = (coverage) => {
    const current = formData.requested_optional_coverages || [];
    if (current.includes(coverage)) {
      updateFormData({ requested_optional_coverages: current.filter(c => c !== coverage) });
    } else {
      updateFormData({ requested_optional_coverages: [...current, coverage] });
    }
  };

  const isOptionalSelected = (coverage) => {
    return (formData.requested_optional_coverages || []).includes(coverage);
  };

  // Get today's date for min date
  const today = new Date().toISOString().split('T')[0];

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-[#232323] text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6" />
          <div>
            <CardTitle className="text-xl">Coverage Start Date & Limits</CardTitle>
            <CardDescription className="text-white/60">
              Select your policy start date and coverage limits
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Policy Effective Date */}
          <div className="p-4 bg-[#EEF0FF] border border-[#7B6FE8]/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-[#7B6FE8] mt-0.5" />
              <div className="flex-1">
                <Label htmlFor="policy_effective_date" className="text-base font-semibold text-[#1A1A1A]">
                  Policy Effective Date *
                </Label>
                <p className="text-sm text-[#6B7280] mb-3">
                  Your coverage will be for a 12-month term starting from this date
                </p>
                <input
                  id="policy_effective_date"
                  type="date"
                  data-testid="policy-effective-date-input"
                  value={formData.policy_effective_date}
                  onChange={(e) => updateFormData({ policy_effective_date: e.target.value })}
                  min={today}
                  required
                  className="h-10 px-3 rounded-md border border-[#7B6FE8]/30 bg-white text-sm w-full max-w-xs"
                />
                <p className="text-xs text-[#7B6FE8] mt-2">
                  Policy term: 12 months
                </p>
              </div>
            </div>
          </div>

          {/* GL Limits */}
          <div>
            <Label className="text-base font-semibold">General Liability Limits *</Label>
            <p className="text-sm text-slate-600 mb-3">Select your preferred coverage limits</p>
            <div className="space-y-3">
              {GL_LIMITS.map((limit, idx) => (
                <div
                  key={idx}
                  data-testid={`gl-limit-${idx}`}
                  onClick={() => updateFormData({ 
                    gl_limit_per_occurrence: limit.occurrence,
                    gl_limit_aggregate: limit.aggregate
                  })}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.gl_limit_per_occurrence === limit.occurrence && 
                    formData.gl_limit_aggregate === limit.aggregate
                      ? 'border-[#4ECDC4] bg-[#EEF0FF]'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`font-medium ${
                        formData.gl_limit_per_occurrence === limit.occurrence ? 'text-[#1A1A1A]' : 'text-slate-900'
                      }`}>
                        {limit.occurrence} / {limit.aggregate}
                      </p>
                      <p className="text-sm text-slate-600">
                        Per Occurrence / Aggregate
                      </p>
                    </div>
                    {formData.gl_limit_per_occurrence === limit.occurrence && 
                     formData.gl_limit_aggregate === limit.aggregate && (
                      <CheckCircle2 className="h-5 w-5 text-[#4ECDC4]" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Optional Coverages */}
          <div>
            <Label className="text-base font-semibold">Optional Coverage Interests</Label>
            <p className="text-sm text-slate-600 mb-3">
              Are you interested in adding any of the following coverages to your quote? (Optional)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {OPTIONAL_COVERAGES.map((coverage) => (
                <div
                  key={coverage.value}
                  data-testid={`optional-${coverage.value}`}
                  onClick={() => handleOptionalCoverageToggle(coverage.value)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    isOptionalSelected(coverage.value)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isOptionalSelected(coverage.value)
                        ? 'border-purple-500 bg-purple-500'
                        : 'border-slate-300'
                    }`}>
                      {isOptionalSelected(coverage.value) && (
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <span className={`font-medium text-sm ${
                      isOptionalSelected(coverage.value) ? 'text-purple-900' : 'text-slate-700'
                    }`}>
                      {coverage.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="font-medium text-slate-900 mb-2">Coverage Summary</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Policy Start:</span>
                <span className="font-medium text-slate-900">
                  {formData.policy_effective_date || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Policy Term:</span>
                <span className="font-medium text-slate-900">12 months</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">GL Limit:</span>
                <span className="font-medium text-slate-900">
                  {formData.gl_limit_per_occurrence || '—'} / {formData.gl_limit_aggregate || '—'}
                </span>
              </div>
              {formData.requested_optional_coverages?.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Optional Interests:</span>
                  <span className="font-medium text-slate-900">
                    {formData.requested_optional_coverages.length} selected
                  </span>
                </div>
              )}
            </div>
          </div>

          <ThemedButton 
            type="submit"
            data-testid="fitness-coverage-next-btn"
            disabled={!formData.policy_effective_date || !formData.gl_limit_per_occurrence}
            variant="wizard" size="full"
          >
            Next: Claims History
          </ThemedButton>
        </form>
      </CardContent>
    </Card>
  );
};

export default FitnessCoverage;
